import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import {
  getV1CompaniesCompanyIdJobsJobIdJobEventsSimulate,
  postV1CompaniesCompanyIdJobsJobIdJobEventsSimulate,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import connection from '~services/connectionModule';

import { type CalculatedGeofenceTrigger } from './CalculatedGeofenceTrigger';
import JobEventsDiffTable from './JobEventsDiffTable';
import { JobEventSimulation } from './JobEventSimulation';
import JobEventsSingleTable from './JobEventsSingleTable';

const historicalJobEventsCache = new Map<string, JobEventSimulation[]>();

const eventBaseKey = (e: JobEventSimulation) =>
  `${e.occurredAt.startOf('second').toISOString()}-${e.eventName}-${e.eventSource}-${e.loadId ?? ''}`;

/** assigns a unique key per event by appending an occurrence index to the base key */
function assignKeys(
  events: JobEventSimulation[],
): { key: string; event: JobEventSimulation }[] {
  const counts = new Map<string, number>();
  return events.map((e) => {
    const base = eventBaseKey(e);
    const idx = counts.get(base) ?? 0;
    counts.set(base, idx + 1);
    return { key: `${base}#${idx}`, event: e };
  });
}

interface JobEventsTableProps {
  companyId: string;
  jobId: string;
  isDirty: boolean;
  rawTriggers: CalculatedGeofenceTrigger[] | null;
  isActive: boolean;
}

export default function JobEventsTable({
  companyId,
  jobId,
  isDirty,
  rawTriggers,
  isActive,
}: JobEventsTableProps) {
  const [historicalEvents, setHistoricalEvents] = useState<JobEventSimulation[] | null>(
    null,
  );
  const [simulatedEvents, setSimulatedEvents] = useState<JobEventSimulation[]>([]);
  const [eventsLoadingCount, setEventsLoadingCount] = useState(0);
  const eventsLoading = eventsLoadingCount > 0;
  const [eventsError, setEventsError] = useState(false);
  const hasLoadedHistorical = useRef(false);

  const loadHistoricalEvents = useCallback(async () => {
    const cacheKey = `${companyId}:${jobId}`;
    const cached = historicalJobEventsCache.get(cacheKey);
    if (cached !== undefined) {
      setHistoricalEvents(cached);
      return;
    }
    setEventsLoadingCount((c) => c + 1);
    try {
      const response = await getV1CompaniesCompanyIdJobsJobIdJobEventsSimulate({
        path: { 'company-id': companyId, 'job-id': jobId },
      });
      const parsed = (response.data.data ?? []).map((proto) =>
        JobEventSimulation.parse(proto),
      );
      historicalJobEventsCache.set(cacheKey, parsed);
      setHistoricalEvents(parsed);
    } catch (error) {
      setHistoricalEvents([]);
      try {
        connection.handleRequestError(
          error,
          t('error_messages.job_events.failed_to_load'),
        );
      } catch {
        // error already handled (toast + Sentry)
      }
    } finally {
      setEventsLoadingCount((c) => c - 1);
    }
  }, [companyId, jobId]);

  const loadSimulatedEvents = useCallback(
    async (triggers: CalculatedGeofenceTrigger[], signal: AbortSignal) => {
      setEventsError(false);
      setEventsLoadingCount((c) => c + 1);
      try {
        const response = await postV1CompaniesCompanyIdJobsJobIdJobEventsSimulate({
          path: { 'company-id': companyId, 'job-id': jobId },
          body: { triggers: triggers.map((t) => t.toSimulationTrigger()) },
          signal,
        });
        const parsed = (response.data.data ?? []).map((proto) =>
          JobEventSimulation.parse(proto),
        );
        return parsed;
      } catch (error) {
        if (signal.aborted) throw error;

        setEventsError(true);
        try {
          connection.handleRequestError(
            error,
            t('error_messages.job_events.failed_to_simulate'),
          );
        } catch {
          // error already handled (toast + Sentry)
        }

        return null;
      } finally {
        setEventsLoadingCount((c) => c - 1);
      }
    },
    [companyId, jobId],
  );

  // load historical events when tab becomes active
  useEffect(() => {
    if (!isActive) return;
    if (hasLoadedHistorical.current) return;
    hasLoadedHistorical.current = true;
    loadHistoricalEvents();
  }, [isActive, loadHistoricalEvents]);

  // load simulated events when raw triggers change and the user has changed something
  useEffect(() => {
    if (!isActive || !rawTriggers || !isDirty) return;
    const controller = new AbortController();
    const loadAndShowSimulatedEvents = async () => {
      try {
        const parsed = await loadSimulatedEvents(rawTriggers, controller.signal);
        if (parsed) {
          setSimulatedEvents(parsed);
        }
      } catch (error) {
        if (controller.signal.aborted) return;

        throw error;
      }
    };
    void loadAndShowSimulatedEvents();
    return () => controller.abort();
  }, [isActive, rawTriggers, isDirty, loadSimulatedEvents]);

  const showSideBySideDiff = isDirty && historicalEvents !== null;

  const sideBySideDiffRows = useMemo((): {
    historical: JobEventSimulation | null;
    current: JobEventSimulation | null;
    sortTime: number;
  }[] => {
    if (!showSideBySideDiff) return [];
    const byKey = new Map<
      string,
      {
        historical: JobEventSimulation | null;
        current: JobEventSimulation | null;
      }
    >();
    for (const { key, event } of assignKeys(historicalEvents ?? [])) {
      const existing = byKey.get(key) ?? { historical: null, current: null };
      existing.historical = event;
      byKey.set(key, existing);
    }
    for (const { key, event } of assignKeys(simulatedEvents)) {
      const existing = byKey.get(key) ?? { historical: null, current: null };
      existing.current = event;
      byKey.set(key, existing);
    }
    const rows = Array.from(byKey.entries()).map(([, v]) => ({
      ...v,
      sortTime:
        v.current?.occurredAt.valueOf() ?? v.historical?.occurredAt.valueOf() ?? 0,
    }));
    rows.sort((a, b) => a.sortTime - b.sortTime);
    return rows;
  }, [simulatedEvents, historicalEvents, showSideBySideDiff]);

  const eventsForSingleView = useMemo(
    () =>
      showSideBySideDiff ? [] : isDirty ? simulatedEvents : (historicalEvents ?? []),
    [showSideBySideDiff, isDirty, simulatedEvents, historicalEvents],
  );

  return (
    <Box sx={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
      {eventsLoading && (
        <Box
          sx={(theme) => ({
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(10, 24, 32, 0.7)'
                : 'rgba(255, 255, 255, 0.7)',
            zIndex: 10,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={32} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('actions.loading')}
            </Typography>
          </Box>
        </Box>
      )}
      {showSideBySideDiff && (
        <JobEventsDiffTable
          diffRows={sideBySideDiffRows}
          simulatedEvents={simulatedEvents}
          historicalEvents={historicalEvents ?? []}
        />
      )}
      {!showSideBySideDiff && (
        <JobEventsSingleTable
          events={eventsForSingleView}
          loading={eventsLoading}
          error={eventsError}
        />
      )}
    </Box>
  );
}
